import React, { useEffect, useState } from 'react';

import { colorsSelector } from '@redux/colorPalette/selectors';

import { useDispatch, useSelector } from 'react-redux';
import {
  ButtonModalExtensionStyle,
  CloseButtonStyle,
  ModalExtensionContainerStyle,
  RemainingTimeContainer,
  RemainingTimeHeader,
} from './RemainingTime.style';
import { FormattedMessage, useIntl } from 'react-intl';
import { bamSessionModeSelector, sessionEndTimestampSelector } from '@redux/session/selectors';
import RemainingTime from 'components/b2b/Header/components/RemainingTime';
import { Box, Button, Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import socket from 'lib/socket/socket';
import { Booking } from 'shared-types/booking';
import { bookingSelector } from '@redux/booking/selectors';
import { BamSessionMode } from '@redux/session/types';
import { add24Hours } from 'services/helpers/handle24hours';
import analytics from 'lib/analytics/analytics';
import { setCurrentBooking } from '@redux/booking';
import FlashMessage from 'components/features/FlashMessage';
import { setSessionEndTimestamp } from '@redux/session';
import { ProlongationHandlerContainer } from 'components/b2b/ProlongationHandler/ProlongationHandlerContainer';
import { userContextSelector } from '@redux/user/selectors';
import { UserContext } from 'shared-types/user';

export const RemainingTimeBloc: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const sessionEndTimestamp = useSelector(sessionEndTimestampSelector);
  const colors = useSelector(colorsSelector);
  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);
  const booking = useSelector(bookingSelector);
  const bamSessionMode = useSelector(bamSessionModeSelector);
  const userContext = useSelector(userContextSelector)

  const [showFlashMessage, setShowFlashMessage] = useState(false);
  const [updatedSessionEndTimestamp, setUpdatedSessionEndTimestamp] = useState<number | null>(null);

  const toggleExtensionModal = () => {
    setIsExtensionModalOpen(!isExtensionModalOpen);
    analytics.track('prolongation_dialog', { isOpen: !isExtensionModalOpen });
  };

  const handleExtensionSuccess = (newEndSessionTime: number) => {
    setShowFlashMessage(true);
    setUpdatedSessionEndTimestamp(newEndSessionTime);
  };

  const onFlashMessageClose = () => {
    setShowFlashMessage(false);

    if (updatedSessionEndTimestamp !== null && updatedSessionEndTimestamp >= 0) {
      analytics.track('session_handler_confirm_extension', {
        sessionEndTimestamp,
        updatedSessionEndTimestamp,
      });
      dispatch(setSessionEndTimestamp(updatedSessionEndTimestamp));
    }
  };

  useEffect(() => {
    // eslint-disable-next-line complexity
    socket.on('newBookingExtension', (newBooking: Booking) => {
      if (
        Boolean(booking?.bookingId) &&
        newBooking.bookingPrincipalId === booking?.bookingId &&
        sessionEndTimestamp &&
        bamSessionMode === BamSessionMode.AUTO
      ) {
        dispatch(
          setCurrentBooking({
            ...newBooking,
            bookingId: newBooking.id,
          }),
        );
        analytics.track('prolongation_admin_succeed', {
          bookingId: newBooking.id,
          bookingPrincipalId: newBooking.bookingPrincipalId,
        });
        const extensionTimeMS =
          (add24Hours(newBooking.endHour) - add24Hours(newBooking.startHour)) * 60000;
        handleExtensionSuccess(sessionEndTimestamp + extensionTimeMS);
      }
    });

    socket.on('cancelBooking', (cancelledBooking: Booking) => {
      if (
        Boolean(booking?.bookingId) &&
        cancelledBooking.id === booking?.bookingId &&
        sessionEndTimestamp &&
        bamSessionMode === BamSessionMode.AUTO
      ) {
        const diff = add24Hours(cancelledBooking.endHour) - add24Hours(cancelledBooking.startHour);
        dispatch(
          setCurrentBooking({
            ...booking,
            bookingId: cancelledBooking.bookingPrincipalId || cancelledBooking.id,
            endHour: booking.endHour - diff,
            bookingPrincipalId: cancelledBooking.bookingPrincipalId,
          }),
        );
        analytics.track('prolongation_admin_cancel', {
          bookingId: cancelledBooking.id,
          endHour: booking.endHour,
          endHourWithDiff: booking.endHour - diff,
          bookingPrincipalId: cancelledBooking.bookingPrincipalId,
          diff,
        });

        const extensionTimeMS = diff * 60000;
        handleExtensionSuccess(sessionEndTimestamp - extensionTimeMS);
      }
    });

    return () => {
      socket.off('newExtension');
      socket.off('cancelExtension');
    };
  }, []);

  return (
    <RemainingTimeContainer {...{ colors }}>
      <RemainingTimeHeader>
        <FormattedMessage id='home.remaining-time.intro-text' />
        {sessionEndTimestamp && <RemainingTime sessionEndTimestamp={sessionEndTimestamp} />}
      </RemainingTimeHeader>

      {userContext === UserContext.BAM && (
        <>
          <Box sx={{ width: '300px', textAlign: 'center' }}>
            <Button variant='contained' onClick={toggleExtensionModal} sx={ButtonModalExtensionStyle}>
              <FormattedMessage id='home.prolongation.request-prolongation' />
            </Button>
          </Box>
          <Modal
            open={isExtensionModalOpen}
            onClose={toggleExtensionModal}
            style={{ backdropFilter: 'blur(2px)' }}
          >
            <Box sx={ModalExtensionContainerStyle}>
              <Box sx={CloseButtonStyle}>
                <CloseIcon onClick={toggleExtensionModal} />
              </Box>

              <ProlongationHandlerContainer handleExtensionSuccess={handleExtensionSuccess} />
            </Box>
          </Modal>
        </>
      )}

      <FlashMessage
        message={intl.formatMessage({ id: 'flash-messages.prolongation-confirmation' })}
        isShown={showFlashMessage}
        onClose={onFlashMessageClose}
      />
    </RemainingTimeContainer>
  );
};
